/**
 * Shared savings calculation utilities.
 * Single source of truth for computing effective prices and annual savings.
 * Used by Dashboard, Tilbud, Market, and Chat pages.
 */
import { getEnergyBreakdown, calcEnergyTotal, DEFAULT_KWH_YEAR, type TariffData } from '../components/EnergyBreakdown'

export interface BestMarketProvider {
  provider: string
  monthly_price: number
  yearly_savings: number
}

export interface DashboardSavingsResult {
  /** Total yearly savings across all non-accepted categories */
  totalSavings: number
  /** Per-category yearly savings (only non-accepted, only positive) */
  savingsByCategory: Record<string, number>
  /** Best provider per category (winner of savings calculation) */
  bestPerCategory: Record<string, BestMarketProvider>
}

/**
 * Cache for dashboard savings state, persisted to localStorage.
 * Dashboard calls `cacheDashboardSavings` when it computes its state.
 * Chat reads via `getCachedDashboardSavings` and sends to backend as
 * dashboard_state in chat-request body.
 *
 * Why localStorage (not module-level):
 * - React Native Web's Metro bundler wraps modules in closures such that
 *   module-level `let` variables are not reliably shared across screens
 *   that are loaded in different bundles/chunks.
 * - localStorage works universally on web and provides persistence across
 *   tab switches and reloads.
 * - On native, localStorage is available via polyfill (Expo/RN Web).
 *
 * If chat is opened before dashboard, cache is null and chat falls back
 * to backend's market_plans fallback (no savings breakdown).
 */
const CACHE_KEY = 'forbrugeragent_dashboard_savings_v1'
const CACHE_MAX_AGE_MS = 10 * 60 * 1000  // 10 minutes

export function cacheDashboardSavings(result: DashboardSavingsResult): void {
  if (typeof localStorage === 'undefined') return
  try {
    const entry = { result, timestamp: Date.now() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // Ignore write errors (quota, privacy mode, etc)
  }
}

export function getCachedDashboardSavings(): DashboardSavingsResult | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (!entry || typeof entry !== 'object' || !entry.result) return null
    // Expire stale cache (10 min)
    if (typeof entry.timestamp === 'number' && Date.now() - entry.timestamp > CACHE_MAX_AGE_MS) {
      return null
    }
    return entry.result as DashboardSavingsResult
  } catch {
    return null
  }
}

/**
 * Compute the effective monthly price for a quote.
 * Energy quotes are recalculated using real tariff data.
 * Non-energy quotes use monthly_price directly.
 */
export function computeEffectivePrice(
  quote: { monthly_price: number; category: string; details?: Record<string, any> },
  tariffs: TariffData | null,
  yearlyKwh?: number,
): number {
  if (quote.category === 'energy' && tariffs) {
    const bd = getEnergyBreakdown(quote, tariffs, yearlyKwh)
    if (bd) return calcEnergyTotal(bd, tariffs, yearlyKwh || DEFAULT_KWH_YEAR)
  }
  return Number(quote.monthly_price) || 0
}

/**
 * Compute annual savings for a single quote vs the user's current price.
 * Returns undefined if no savings or insufficient data.
 */
export function computeQuoteSavings(
  currentPrice: number,
  quote: { monthly_price: number; category: string; details?: Record<string, any> },
  tariffs: TariffData | null,
  yearlyKwh?: number,
): number | undefined {
  if (!(currentPrice > 0)) return undefined
  const effectivePrice = computeEffectivePrice(quote, tariffs, yearlyKwh)
  if (effectivePrice <= 0) return undefined
  const monthlySaving = currentPrice - effectivePrice
  if (monthlySaving <= 0) return undefined
  return Math.round(monthlySaving * 12)
}

/**
 * Compute dashboard-wide savings summary across all categories.
 * This is the SINGLE SOURCE OF TRUTH for the "Din agent kan spare dig X kr/år"
 * number shown on Oversigt-skærmen. Chat-agent receives this via dashboard_state
 * in request body to ensure consistent numbers across screens.
 *
 * Logic mirrors the savingsByCategory useMemo in app/(tabs)/index.tsx:
 * - Skip categories with accepted quotes
 * - For each non-accepted category: find the best price across personal
 *   quotes + market plans, compute yearly savings vs current agreement
 * - Return totals + per-category detail
 */
export function computeDashboardSavings(
  quotes: Array<{ provider: string; monthly_price: number; category: string; status: string; details?: Record<string, any> }>,
  marketPlans: Record<string, Array<{ provider: string; monthly_price?: number; monthly_subscription?: number; kwh_surcharge?: number }>>,
  currentAgreements: Record<string, { price: number; yearlyKwh?: number }>,
  tariffs: TariffData | null,
): DashboardSavingsResult {
  const savingsByCategory: Record<string, number> = {}
  const bestPerCategory: Record<string, BestMarketProvider> = {}

  const acceptedCats = new Set(
    quotes.filter((q) => q.status === 'accepted').map((q) => q.category)
  )

  for (const cat of ['energy', 'mobile', 'internet']) {
    if (acceptedCats.has(cat)) continue
    const agreement = currentAgreements[cat]
    if (!agreement || agreement.price <= 0) continue
    const yearlyKwh = cat === 'energy' ? agreement.yearlyKwh : undefined

    let best = 0
    let bestProvider: BestMarketProvider | null = null

    // Personal quotes first
    const catQuotes = quotes.filter((q) => q.category === cat && q.status !== 'accepted')
    const personalProviders = new Set(catQuotes.map((q) => q.provider?.toLowerCase()))
    for (const q of catQuotes) {
      const saving = computeQuoteSavings(agreement.price, q, tariffs, yearlyKwh)
      if (saving && saving > best) {
        best = saving
        bestProvider = {
          provider: q.provider,
          monthly_price: Math.round(computeEffectivePrice(q, tariffs, yearlyKwh)),
          yearly_savings: saving,
        }
      }
    }

    // Market plans — dedup by provider, compute effective price, compare
    for (const p of (marketPlans[cat] || [])) {
      if (personalProviders.has(p.provider?.toLowerCase())) continue
      let price: number
      if (cat === 'energy' && tariffs) {
        const bd = getEnergyBreakdown(
          { details: { subscription: p.monthly_subscription, surcharge_ore: p.kwh_surcharge } },
          tariffs,
          yearlyKwh,
        )
        price = bd ? calcEnergyTotal(bd, tariffs, yearlyKwh || DEFAULT_KWH_YEAR) : Number(p.monthly_price || 0)
      } else {
        price = Number(p.monthly_price ?? p.monthly_subscription ?? 0)
      }
      if (price <= 0 || price >= agreement.price) continue
      const saving = Math.round((agreement.price - price) * 12)
      if (saving > best) {
        best = saving
        bestProvider = {
          provider: p.provider,
          monthly_price: Math.round(price),
          yearly_savings: saving,
        }
      }
    }

    if (best > 0) {
      savingsByCategory[cat] = best
      if (bestProvider) bestPerCategory[cat] = bestProvider
    }
  }

  const totalSavings = Object.values(savingsByCategory).reduce((sum, s) => sum + s, 0)

  return { totalSavings, savingsByCategory, bestPerCategory }
}
